import { cn } from '@/lib/utils'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { motion } from 'motion/react'
import { memo, useCallback, useMemo } from 'react'
import { InstagramOverlay } from '../../constructs'
import { logger } from '../../utilities'
import { ColorProvider } from '../../views'
import { AiGeneratedWatermark } from '../ai-generated-label'
import { CanvasContainer, type ExternalCanvasData, initialCanvasData } from '../canvas'
import { CanvasData } from '../canvas/data'
import { CampaignImage, EmptyImageHint, useImageCache, useIsImageSlotEmpty } from '../images'
import { ImageOverlay } from '../images/image-overlay'
import { Transformer } from '../simulator-tools'
import {
	activeStoryImageToolAtom,
	campaignDetailsBrandingAtom,
	campaignDetailsLogoAtom,
	customFontsAtom,
	customFontsLoadingAtom,
	editorFocusTargetAtom,
	orgAssetsAtom,
	showCanvasToolbarAtom,
	storyAdAiImageryDetectedAtom,
	storyAdAtom,
	storyAdCanvasLoadCounterAtom,
	storyAdCtaTextAtom,
	storyAdHasGeneratedContentAtom,
	storyAdImageIdAtom,
	storyAdLocationAtom,
	storyAdMetaBackdropTransformAtom,
	storyAdMetaCanvasDocumentAtom,
	storyAdMetaCanvasEnabledAtom,
	storyAdMetaForceAiGeneratedLabelAtom,
	storyAdMetaGeneralContentHideAtom,
	storyAdMetaLogoBackgroundFillColorAtom,
	storyAdMetaOverlayAtom,
	storyAdMetaTextColorAtom,
	storyAdStatusAtom,
	storyAdSubtitleAtom,
	storyAdTitleAtom,
} from '../state'
import { SafeZoneGuide } from './safe-zone-guide'
import { StoryAdContentLayer } from './story-ad-content-layer'

// The image is sized against these too, so they must stay the card's exact size or the
// backdrop shows around it.
const FRAME_WIDTH = 480
const FRAME_HEIGHT = Math.round((16 / 9) * FRAME_WIDTH)

const fallbackPalette = {
	foreground: 'rgba(255,255,255,1)',
	background: 'rgba(0,0,0,1)',
	accentForeground: 'rgba(255,255,255,1)',
	accentBackground: 'rgba(0,0,0,1)',
}

export const StoryAdSimulator = memo(function StoryAdSimulator() {
	const storyAd = useAtomValue(storyAdAtom)
	const branding = useAtomValue(campaignDetailsBrandingAtom)
	const imageId = useAtomValue(storyAdImageIdAtom)
	const textColor = useAtomValue(storyAdMetaTextColorAtom)
	const logoBackgroundFillColor = useAtomValue(storyAdMetaLogoBackgroundFillColorAtom)
	const overlay = useAtomValue(storyAdMetaOverlayAtom)
	const forceAiGeneratedLabel = useAtomValue(storyAdMetaForceAiGeneratedLabelAtom)
	const setAiImageryDetected = useSetAtom(storyAdAiImageryDetectedAtom)

	const storyAdStatus = useAtomValue(storyAdStatusAtom)
	const hasGeneratedContent = useAtomValue(storyAdHasGeneratedContentAtom)
	const isImageToolExecuting = useAtomValue(activeStoryImageToolAtom)
	const isGeneratingText = storyAdStatus === 'generating' && !hasGeneratedContent
	// Text should flash during generation or text tool execution (not image tools)
	const shouldFlashContent = ((storyAdStatus === 'generating' && hasGeneratedContent)
		|| storyAdStatus === 'tool-executing') && !isImageToolExecuting
	// Image should flash during image tool execution
	const shouldFlashImage = isImageToolExecuting

	const [backdropTransform, setBackdropTransform] = useAtom(storyAdMetaBackdropTransformAtom)
	const focusTarget = useAtomValue(editorFocusTargetAtom)

	const hide = useAtomValue(storyAdMetaGeneralContentHideAtom)

	// Canvas state
	const canvasEnabled = useAtomValue(storyAdMetaCanvasEnabledAtom)
	const canvasDocument = useAtomValue(storyAdMetaCanvasDocumentAtom)
	const setCanvasDocument = useSetAtom(storyAdMetaCanvasDocumentAtom)
	const canvasLoadCounter = useAtomValue(storyAdCanvasLoadCounterAtom)
	const showCanvasToolbar = useAtomValue(showCanvasToolbarAtom)

	// External canvas data atoms
	const [title, setTitle] = useAtom(storyAdTitleAtom)
	const [subtitle, setSubtitle] = useAtom(storyAdSubtitleAtom)
	const [location, setLocation] = useAtom(storyAdLocationAtom)
	const [ctaText, setCtaText] = useAtom(storyAdCtaTextAtom)
	const logo = useAtomValue(campaignDetailsLogoAtom)
	const orgAssets = useAtomValue(orgAssetsAtom)
	const customFonts = useAtomValue(customFontsAtom)
	const customFontsLoading = useAtomValue(customFontsLoadingAtom)

	const { getImage } = useImageCache()
	const backgroundUrl = getImage(imageId ?? '').url
	const hasImage = backgroundUrl !== ''
	const isImageSlotEmpty = useIsImageSlotEmpty(imageId)
	const logoUrl = logo?.url ?? ''

	const externalCanvasData = useMemo<ExternalCanvasData>(() => ({
		title: title ?? '',
		subtitle: subtitle ?? '',
		location: location ?? '',
		cta: ctaText ?? '',
		logoUrl,
		backgroundUrl,
		foregroundColor: branding?.foreground ?? fallbackPalette.foreground,
		backgroundColor: branding?.background ?? fallbackPalette.background,
		accentForegroundColor: branding?.accentForeground ?? fallbackPalette.accentForeground,
		accentBackgroundColor: branding?.accentBackground ?? fallbackPalette.accentBackground,
	}), [title, subtitle, location, ctaText, logoUrl, backgroundUrl, branding])

	const handleExternalCanvasDataChange = useCallback((partial: Partial<ExternalCanvasData>) => {
		if (partial.title !== undefined) { setTitle(partial.title) }
		if (partial.subtitle !== undefined) { setSubtitle(partial.subtitle) }
		if (partial.location !== undefined) { setLocation(partial.location) }
		if (partial.cta !== undefined) { setCtaText(partial.cta) }
	}, [setTitle, setSubtitle, setLocation, setCtaText])

	const parsedCanvasDocument = useMemo(() => {
		if (typeof canvasDocument !== 'string') { return initialCanvasData }
		try {
			const raw = JSON.parse(canvasDocument)
			const result = CanvasData.safeParse(raw)
			if (!result.success) {
				logger.error(
					"Canvas document schema validation failed — falling back to default. User's saved design was discarded.",
					{
						category: 'canvas',
						data: {
							simulator: 'story',
							zodErrors: result.error.issues,
							rawLayerTypes: raw?.layers ? Object.values(raw.layers).map((l: any) => l?.type) : [],
						},
					},
				)
				return initialCanvasData
			}
			return result.data
		} catch (err) {
			logger.error(
				"Failed to parse canvas document JSON — falling back to default. User's saved design was discarded.",
				{
					category: 'canvas',
					data: { simulator: 'story', error: String(err) },
				},
			)
			return initialCanvasData
		}
	}, [canvasDocument])

	const handleCanvasDocumentChange = useCallback((doc: CanvasData) => {
		setCanvasDocument(JSON.stringify(doc))
	}, [setCanvasDocument])

	// Convert text color meta value to CSS
	const textColorValue = textColor === 'white'
		? '#ffffff'
		: textColor === 'black'
		? '#000000'
		: (branding?.foreground ?? fallbackPalette.foreground)

	if (!storyAd || !backdropTransform || !overlay) {
		return null
	}

	return (
		<ColorProvider brandingPalette={branding ?? fallbackPalette}>
			<div
				className="relative z-10"
				style={{
					'--logo-background-fill-color': logoBackgroundFillColor,
				} as React.CSSProperties}
			>
				<motion.div
					initial={{
						scale: 0.98,
						translateY: 10,
						opacity: 0,
					}}
					animate={{
						scale: 1,
						translateY: 0,
						opacity: 1,
						backgroundColor: branding?.background ?? fallbackPalette.background,
						color: textColorValue,
					}}
					style={{
						width: FRAME_WIDTH,
						height: FRAME_HEIGHT,
					}}
					transition={{
						duration: 0.3,
						translateX: { 'duration': 0.9, 'bounce': 0.3, type: 'spring' },
					}}
					className="ring perspective-dramatic ring-grey-200 bg-white rounded-sm shadow-lg overflow-hidden"
					data-render-me={true}
					data-screenshot-font-fix
				>
					{canvasEnabled
						? (
							<>
								<CanvasContainer
									key={canvasLoadCounter}
									width={FRAME_WIDTH}
									height={FRAME_HEIGHT}
									document={parsedCanvasDocument}
									onDocumentChange={handleCanvasDocumentChange}
									externalCanvasData={externalCanvasData}
									onExternalCanvasDataChange={handleExternalCanvasDataChange}
									safeZone={{ top: 100, right: 0, bottom: 120, left: 0 }}
									orgAssets={orgAssets}
									customFonts={customFonts}
									customFontsLoading={customFontsLoading}
									isGenerating={isGeneratingText}
									simulatorType="story"
									showToolbar={showCanvasToolbar}
								/>
								{isImageSlotEmpty && <EmptyImageHint />}

								<SafeZoneGuide />
								<InstagramOverlay />
							</>
						)
						: (
							<>
								<div
									className={cn(
										'absolute inset-0 flex items-center justify-center',
										shouldFlashImage && 'animate-pulse',
									)}
								>
									<Transformer
										value={backdropTransform}
										onChange={setBackdropTransform}
										passthroughClassName=""
										enableTransform={focusTarget.target === 'story-ad(backdrop)'}
									>
										<div className="size-full grid relative">
											<CampaignImage
												imageId={imageId ?? ''}
												containerWidth={FRAME_WIDTH}
												containerHeight={FRAME_HEIGHT}
												color={branding?.background ?? fallbackPalette.background}
											/>
										</div>
									</Transformer>
								</div>

								{hasImage && <ImageOverlay overlay={overlay} />}

								<div className="relative z-10 h-full flex flex-col justify-between px-4 py-2">
									{!hide && (
										<StoryAdContentLayer
											isGeneratingText={isGeneratingText}
											shouldFlashContent={shouldFlashContent}
										/>
									)}

									<SafeZoneGuide />
									<InstagramOverlay />
								</div>
							</>
						)}
					<AiGeneratedWatermark
						labelClassName="right-3 bottom-32"
						forceVisible={forceAiGeneratedLabel}
						onDetectedChange={setAiImageryDetected}
					/>
				</motion.div>
			</div>
		</ColorProvider>
	)
})
